import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faBars,
  faSignOutAlt,
  faEnvelope,
  faLocationDot,
  faFileInvoice,
  faUserPlus,
  faSync,
  faSearch,
  faUser,
  faQuestionCircle,
  faInfoCircle,
  faMapMarkerAlt,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { APP_ROUTES } from '../config/api.config';
import Loader from '../components/Loader';
import { getSubscribedLocations, cancelSubscription, initiateGHLAuth, getSubscriptionPlans, createSubscription, createTestSubscription, Plan } from '../services/api';
import Footer from '../components/Footer';
import noDataFound from '../assets/images/no-data-found.png';
import noResults from '../assets/images/no-result.jpg';
import { useLogoutHandler } from '../utils/logoutHandler';
import LogoutConfirmDialog from '../components/LogoutConfirmDialog';
import { toast } from 'react-hot-toast';
import Sidebar from '../components/Sidebar/Sidebar';
import logoMain from '../assets/images/logo-main.png';

const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f5f6fa;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const MobileHeader = styled.div`
  display: none;
`;

const HamburgerButton = styled.button`
  background: none;
  border: none;
  color: #333;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.3s ease;
  border: 1px solid #e0e0e0;

  &:hover {
    background: rgba(255, 107, 107, 0.1);
  }
`;

const MainContent = styled.div<{ isCollapsed: boolean; isMobileOpen: boolean }>`
  flex: 1;
  margin-left: ${props => props.isCollapsed ? '70px' : '240px'};
  padding: 0;
  padding-bottom: 60px;
  padding-top: 76px;
  transition: margin-left 0.3s ease;
  width: calc(100% - ${props => props.isCollapsed ? '70px' : '240px'});

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    padding-top: 76px;
    transition: none;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  padding: 12px;
  background: #f8d7da;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const TopBar = styled.div<{ isCollapsed: boolean; isMobileOpen: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: white;
  border-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 24px;
  position: fixed;
  top: 0;
  right: 0;
  left: ${props => props.isCollapsed ? '70px' : '240px'};
  z-index: 100;
  transition: left 0.3s ease;

  @media (max-width: 768px) {
    left: 0;
    width: 100%;
    transition: none;
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: #333;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.3s ease;
  border: 1px solid #e0e0e0;

  &:hover {
    background: rgba(255, 107, 107, 0.1);
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 107, 107, 0.1);
  }
  
  @media (max-width: 768px) {
    justify-content: flex-end;
    padding: 0;
    gap: 8px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const UserName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #333;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #ff6b6b;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  margin-left: 8px;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const UserDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  display: ${props => props.isOpen ? 'block' : 'none'};
  z-index: 1000;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f8f9fa;
  }

  svg {
    width: 16px;
    margin-right: 12px;
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background-color: #e8e8e8;
  margin: 4px 0;
`;

const UserEmail = styled.div`
  font-size: 15px;
  color: #666;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    width: 16px;
    color: #666;
  }
`;

const LogoutMenuItem = styled.button<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  color: white;
  font-size: 15px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  svg {
    width: 20px;
    margin-right: ${props => props.$isCollapsed ? '0' : '12px'};
  }

  span {
    display: ${props => props.$isCollapsed ? 'none' : 'block'};
  }
`;

const PageContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow-x: auto;
  margin: 0 20px 30px 20px;
  width: calc(100% - 40px);

  @media (max-width: 1200px) {
    margin: 0 20px 25px 20px;
  }

  @media (max-width: 768px) {
    padding: 15px;
    border-radius: 8px;
    margin: 0 15px 20px 15px;
    width: calc(100% - 30px);
  }
`;

const TableHeader = styled.div`
  margin-bottom: 20px;
`;

const TableTitle = styled.h1`
  font-size: 24px;
  color: #2d3748;
  margin: 0;
  margin-bottom: 16px;
`;

const TableControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  @media (max-width: 992px) {
    gap: 12px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const EntriesControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;
`;

const Select = styled.select`
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  color: #666;
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
`;

const SearchControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-width: 200px;

  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 8px;
  top: 42%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #aaa;
  font-size: 16px;
  cursor: pointer;
  padding: 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  &:hover {
    color: #ff6b6b;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  table-layout: fixed;

  @media (max-width: 992px) {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  border-bottom: 2px solid #e9ecef;
  color: #6c757d;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #f8f9fa;
  }

  @media (max-width: 992px) {
    padding: 10px 8px;
    font-size: 14px;
  }

  @media (max-width: 768px) {
    padding: 8px 6px;
    font-size: 13px;
  }
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
  color: #495057;
  word-break: break-word;

  @media (max-width: 992px) {
    padding: 10px 8px;
    font-size: 14px;
  }

  @media (max-width: 768px) {
    padding: 8px 6px;
    font-size: 13px;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background: ${props => {
    switch (props.status.toLowerCase()) {
      case 'active':
        return '#28a745';
      case 'authenticated':
        return '#17a2b8';
      case 'cancelled':
        return '#dc3545';
      case 'created':
        return '#ffc107';
      case 'failed':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  }};

  @media (max-width: 768px) {
    padding: 3px 6px;
    font-size: 11px;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 30px 0;
  flex-wrap: wrap;
  width: 100%;

  @media (max-width: 768px) {
    margin: 20px 0;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
    flex-wrap: wrap;
    justify-content: center;
  width: 100%;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const PaginationButton = styled.button<{ active?: boolean }>`
  padding: 8px 12px;
  border: 1px solid ${props => props.active ? '#ff6b6b' : '#dee2e6'};
  background: ${props => props.active ? '#ff6b6b' : 'white'};
  color: ${props => props.active ? 'white' : '#666'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  min-width: 36px;

  &:hover {
    background: ${props => props.active ? '#ff6b6b' : '#fff0f0'};
    border-color: #ff6b6b;
    color: ${props => props.active ? 'white' : '#ff6b6b'};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: #f8f9fa;
    border-color: #dee2e6;
    color: #666;
  }

  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 13px;
    min-width: 32px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin: 24px 0;
`;

const EmptyStateImage = styled.img`
  width: 660px;
  height: auto;
  margin-bottom: 24px;
`;

const EmptyImage = styled.img`
    width: 70%;
  height: auto;
  margin-bottom: 24px;
`;

const ErrorImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin: 24px 0;
`;

const TableActionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 190px;
  max-width: 220px;
`;

const TableActionButton = styled.button<{ variant?: 'whatsapp' | 'cancel' }>`
  width: 100%;
  min-height: 38px;
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  text-align: center;
  box-sizing: border-box;

  ${({ variant }) =>
    variant === 'whatsapp'
      ? `
    background: linear-gradient(135deg, #25D366, #128C7E);
    color: white;

    &:hover:not(:disabled) {
      box-shadow: 0 2px 8px rgba(37, 211, 102, 0.25);
    }
  `
      : `
    background: linear-gradient(135deg, #ff6b6b, #ff8e53);
    color: white;

    &:hover:not(:disabled) {
      box-shadow: 0 2px 8px rgba(255, 107, 107, 0.2);
    }
  `}

  &:disabled {
    background: #ccc;
    color: white;
    opacity: 0.7;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const CancelButton = styled.button`
  background: linear-gradient(135deg, #ff6b6b, #ff8e53);
  color: white;
  padding: 8px 20px;
  border-radius: 25px;
  font-weight: 600;
  font-size: 14px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  margin: 0 4px;

  &:hover {
    background: #ff8e53;
    color: #fff;
    box-shadow: 0 2px 8px rgba(255, 107, 107, 0.15);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
  animation: fadeIn 0.3s ease-out;
  padding: 20px;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  width: 90%;
  max-width: 600px;
  max-height: 85vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    width: 95%;
    padding: 20px;
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #edf2f7;
`;

const ModalTitle = styled.h2`
  color: #2d3748;
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0;
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #718096;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background: #f7fafc;
    color: #ff6b6b;
  }
`;

const OuterBox = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: calc(100% - 40px);
  max-width: 100%;
  margin: 24px 20px;
  overflow-x: visible;

  @media (max-width: 1200px) {
    padding: 20px;
    margin: 24px 15px;
    width: calc(100% - 30px);
  }

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 8px;
    margin: 24px 15px;
    width: calc(100% - 30px);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 2rem;
  width: 100%;
  max-width: 100%;
  padding: 0 5px;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const Label = styled.label`
  display: block;
  color: #4a5568;
  font-size: 0.95rem;
  font-weight: 500;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-left: 5px;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${props => props.hasError ? '#ff6b6b' : '#e2e8f0'};
  border-radius: 4px;
  font-size: 1rem;
  transition: all 0.2s;
  background: white;
  max-width: 500px;
  margin: 0 5px;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ff6b6b' : '#ff6b6b'};
  }

  &::placeholder {
    color: #a0aec0;
  }

  @media (max-width: 768px) {
    padding: 0.625rem 0.875rem;
    font-size: 0.95rem;
    max-width: 100%;
    margin: 0;
  }
`;

const InfoText = styled.p`
  color: #718096;
  font-size: 1rem;
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 500px;
  width: 100%;

  svg {
    color: #ff6b6b;
    font-size: 0.9rem;
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
    max-width: 100%;
  }
`;

const ErrorText = styled.p`
  color: #ff6b6b;
  font-size: 0.95rem;
  margin-top: 0.75rem;
  margin-left: 5px;
  width: 100%;
  max-width: 500px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
  max-width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1rem;
    width: 100%;
    padding: 0 5px;
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 2rem;
  border-radius: 25px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  white-space: nowrap;
  max-width: 240px;
  width: 100%;
  
  ${props => props.variant === 'primary' ? `
    background: #ff6b6b;
    color: white;
    border: none;
    
    &:hover {
      background: #ff8e53;
    }
  ` : `
    background: transparent;
    color: #4a5568;
    border: none;
    
    &:hover {
      color: #2d3748;
    }
  `}

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    padding: 0.625rem 1.5rem;
    font-size: 0.9rem;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 2rem;
  border-radius: 25px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  
  ${props => props.variant === 'primary' ? `
    background: #ff6b6b;
    color: white;
    border: none;
    
    &:hover {
      background: #ff8e53;
    }
  ` : `
    background: transparent;
    color: #4a5568;
    border: none;
    
    &:hover {
      color: #2d3748;
    }
  `}

  @media (max-width: 768px) {
    width: 100%;
    padding: 0.625rem 1.5rem;
    font-size: 0.9rem;
  }
`;

const PlansList = styled.div`
  display: flex;
  gap: 2rem;
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const PlanItem = styled.div`
  border: 2px solid #ff6b6b;
  border-radius: 20px;
  padding: 2rem;
  min-width: 300px;
  max-width: 400px;
  width: 100%;
  margin-bottom: 1.5rem;
  background: #fff;
  box-shadow: 0 4px 24px rgba(255,107,107,0.08);
  position: relative;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 107, 107, 0.1);
    border-color: #ff6b6b;
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 1200px) {
    padding: 1.5rem;
    min-width: 260px;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    min-width: 100%;
    max-width: 100%;
  }
`;

const RequiredAsterisk = styled.span`
  color: #ff4646;
  margin-left: 2px;
  font-size: 1em;
  display: inline-block;
`;

const InfoIconButton = styled.button`
  background: none;
  border: none;
  padding: 0 4px;
  margin-left: 6px;
  color: #ff6b6b;
  cursor: pointer;
  font-size: 1em;
  display: inline-flex;
  align-items: center;
  transition: color 0.2s;
  &:hover {
    color: #ff8e53;
  }

  @media (max-width: 480px) {
    margin-left: 4px;
  }
`;

const ModalFooter = styled.div`
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #edf2f7;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    margin-top: 20px;
    padding-top: 12px;
  }
`;

const FormContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin: 24px 20px 30px 20px;
  width: calc(100% - 40px);

  @media (max-width: 1200px) {
    margin: 15px 20px 25px 20px;
  }

  @media (max-width: 768px) {
    padding: 15px;
    border-radius: 8px;
    margin: 10px 15px 20px 15px;
    width: calc(100% - 30px);
  }
`;

const MobileLogo = styled.div`
  width: 120px;
  height: auto;
  margin-left: 16px;
`;

interface Location {
  id: string;
  locationId: string;
  locationName: string;
  subscriptionDate: string;
  dueDate: string;
  status: string;
  planName: string;
  razorpaySubscriptionId: string;
  isTrialActive?: boolean;
  trialEndDate?: string;
  actualDueDate?: string;
}

interface ExtendedPlan extends Plan {
  isTrial?: boolean;
  trialDays?: number;
}

type PaymentResponseData = {
  subscriptionId?: string;
  orderId?: string;
  amount?: number;
  currency?: string;
  status?: string;
  [key: string]: any;
};

declare global {
  interface Window {
    Razorpay: any;
  }
}

const SubscribedLocations: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [entriesPerPage, setEntriesPerPage] = useState('10');
  const [searchTerm, setSearchTerm] = useState('');
  const [locations, setLocations] = useState<Location[]>([]);
  const [rawSubscriptions, setRawSubscriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddLocationLoading, setIsAddLocationLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<Location | null>(null);
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const navigate = useNavigate();

  const {
    showLogoutConfirm,
    handleLogoutClick,
    handleLogoutConfirm,
    handleLogoutCancel
  } = useLogoutHandler();

  const [addLocationId, setAddLocationId] = useState('');
  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const [isPlansModalOpen, setIsPlansModalOpen] = useState(false);
  const [showLocationIdInfo, setShowLocationIdInfo] = useState(false);
  const [plansError, setPlansError] = useState('');
  const [subscriptionError, setSubscriptionError] = useState('');
  const [isUpgradeMode, setIsUpgradeMode] = useState(false);

  const [plans, setPlans] = useState<ExtendedPlan[]>([]);
  const [isPlansLoading, setIsPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [planPeriod, setPlanPeriod] = useState<'Monthly' | 'Yearly'>('Monthly');

  // Fixed: Add state for 11+ plans with proper logic
  const [subAccountCount, setSubAccountCount] = useState(11);
  const [isFirst11Plus, setIsFirst11Plus] = useState(false);
  const [selectedPlanName, setSelectedPlanName] = useState('');
  const [isActivatingTestSub, setIsActivatingTestSub] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setIsLoading(true);
        const response = await getSubscribedLocations();

        setRawSubscriptions(response.subscriptions);

        const formattedLocations = response.subscriptions.flatMap((subscription) =>
          subscription.subAccounts.map((subAccount) => {
            // Calculate due date based on trial status
            let dueDate: string;
            const subscriptionCreatedDate = new Date(subscription.subscriptionCreatedDate);
            const subscriptionEndDate = new Date(subscription.subscriptionEndDate);

            // Check if this is a trial subscription (14 days from creation)
            const trialEndDate = new Date(subscriptionCreatedDate);
            trialEndDate.setDate(trialEndDate.getDate() + 14);

            const now = new Date();
            const isTrialActive = now < trialEndDate && subscription.status.toLowerCase() === 'authenticated';

            if (isTrialActive) {
              // Show trial end date
              dueDate = trialEndDate.toLocaleDateString();
            } else {
              // Show actual subscription end date
              dueDate = subscriptionEndDate.toLocaleDateString();
            }

            return {
              id: subAccount._id,
              locationId: subAccount.locationId,
              locationName: subAccount.locationName,
              subscriptionDate: subscriptionCreatedDate.toLocaleDateString(),
              dueDate: dueDate,
              status: subscription.status,
              planName: subscription.planName,
              razorpaySubscriptionId: subscription.razorpaySubscriptionId,
              isTrialActive: isTrialActive,
              trialEndDate: trialEndDate.toLocaleDateString(),
              actualDueDate: subscriptionEndDate.toLocaleDateString(),
            };
          })
        );

        setLocations(formattedLocations);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : "An error occurred while fetching locations";
        setError(errorMsg);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLocations();
  }, []);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userName = user.name || '';

  const handleLogout = () => {
    handleLogoutClick();
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const filteredLocations = locations
    .filter(location =>
      Object.values(location).some(value =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    )
    .sort((a, b) => {
      if (!sortColumn) return 0;
      let aValue = a[sortColumn as keyof Location];
      let bValue = b[sortColumn as keyof Location];

      // Handle undefined values
      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return sortDirection === 'asc' ? -1 : 1;
      if (bValue === undefined) return sortDirection === 'asc' ? 1 : -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      if (aValue === bValue) return 0;
      if (sortDirection === 'asc') {
        return (aValue as any) > (bValue as any) ? 1 : -1;
      } else {
        return (aValue as any) < (bValue as any) ? 1 : -1;
      }
    });

  const handleOpenCancelModal = (location: Location) => {
    setSelectedSubscription(location);
    setCancelError(null);
    setCancelModalOpen(true);
  };

  const handleCloseCancelModal = () => {
    setCancelModalOpen(false);
    setSelectedSubscription(null);
    setCancelError(null);
  };

  const handleCancelSubmit = async () => {
    if (!selectedSubscription) return;
    setCancelLoading(true);
    setCancelError(null);
    try {
      await cancelSubscription(selectedSubscription.razorpaySubscriptionId, false);
      setCancelModalOpen(false);
      const response = await getSubscribedLocations();
      const formattedLocations = response.subscriptions.flatMap((subscription) =>
        subscription.subAccounts.map((subAccount) => {
          // Calculate due date based on trial status
          let dueDate: string;
          const subscriptionCreatedDate = new Date(subscription.subscriptionCreatedDate);
          const subscriptionEndDate = new Date(subscription.subscriptionEndDate);

          // Check if this is a trial subscription (14 days from creation)
          const trialEndDate = new Date(subscriptionCreatedDate);
          trialEndDate.setDate(trialEndDate.getDate() + 14);

          const now = new Date();
          const isTrialActive = now < trialEndDate && subscription.status.toLowerCase() === 'authenticated';

          if (isTrialActive) {
            // Show trial end date
            dueDate = trialEndDate.toLocaleDateString();
          } else {
            // Show actual subscription end date
            dueDate = subscriptionEndDate.toLocaleDateString();
          }

          return {
            id: subAccount._id,
            locationId: subAccount.locationId,
            locationName: subAccount.locationName,
            subscriptionDate: subscriptionCreatedDate.toLocaleDateString(),
            dueDate: dueDate,
            status: subscription.status,
            planName: subscription.planName,
            razorpaySubscriptionId: subscription.razorpaySubscriptionId,
            isTrialActive: isTrialActive,
            trialEndDate: trialEndDate.toLocaleDateString(),
            actualDueDate: subscriptionEndDate.toLocaleDateString(),
          };
        })
      );
      setLocations(formattedLocations);
    } catch (err: any) {
      setCancelError(err.message || 'Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  const getSortArrow = (column: string) => {
    if (sortColumn === column) {
      return sortDirection === 'asc' ? '▲' : '▼';
    }
    return '△';
  };

  useEffect(() => {
    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = resolve;
        document.body.appendChild(script);
      });
    };
    loadRazorpayScript();
  }, []);

  const openRazorpay = ({
    orderId,
    amount,
    currency,
    subscriptionId
  }: {
    orderId?: string;
    amount?: number;
    currency?: string;
    subscriptionId?: string;
  }) => {
    if (!orderId && !subscriptionId) {
      console.error("❌ Neither Order ID nor Subscription ID is present! Cannot open Razorpay.");
      return;
    }

    const options: any = {
      key: "rzp_test_BpaLaGViQSOHnn",
      name: "The One Technologies",
      description: orderId ? "Dynamic Sub-Account Payment" : "Subscription Payment",
      prefill: {
        name: user?.name || "User",
        email: user?.email || "user@example.com",
      },
      theme: { color: "#F37254" },
      handler: function (response: any) {
        console.log('Payment successful:', response);
        toast.success("Payment Successful!");
        navigate(APP_ROUTES.DASHBOARD);
      },
      modal: {
        ondismiss: function () {
          console.log('Payment modal dismissed');
        }
      },
      notes: {}
    };

    if (orderId) {
      if (typeof amount !== 'number' || !currency) {
        toast.error('Payment information missing. Please try again.');
        return;
      }
      options.order_id = orderId;
      options.amount = amount * 100;
      options.currency = currency;
      options.notes.order_id = orderId;
    } else if (subscriptionId) {
      options.subscription_id = subscriptionId;
      options.notes.subscription_id = subscriptionId;
    }

    try {
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Error opening Razorpay:', error);
      toast.error('Failed to open payment window');
    }
  };

  // Fixed: Improved subscription state detection
  const getUserSubscriptionState = (locations: Location[]) => {
    // Check if user has any active subscriptions without sub-accounts
    const hasValidSubscriptionWithoutSubaccounts = rawSubscriptions.some(subscription =>
      subscription.subAccounts.length === 0 &&
      ['active', 'authenticated', 'created'].includes(subscription.status.toLowerCase())
    );

    if (hasValidSubscriptionWithoutSubaccounts) {
      const subscription = rawSubscriptions.find(sub =>
        sub.subAccounts.length === 0 &&
        ['active', 'authenticated', 'created'].includes(sub.status.toLowerCase())
      );

      if (subscription.status.toLowerCase() === 'active') {
        return 'ACTIVE';
      }
      return 'PENDING_PAYMENT';
    }

    if (locations.length === 0) {
      return 'FREE_TRIAL';
    }

    const hasActiveSubscription = locations.some(loc => loc.status.toLowerCase() === 'active');
    const hasCreatedOrAuthSubscription = locations.some(
      loc => ['created', 'authenticated'].includes(loc.status.toLowerCase())
    );

    if (hasActiveSubscription) {
      return 'ACTIVE';
    } else if (hasCreatedOrAuthSubscription) {
      return 'PENDING_PAYMENT';
    }

    return 'FREE_TRIAL';
  };

  // Fixed: Improved plan selection logic
  const handleSelectPlan = async (planId: string, planName: string) => {
    setSelectedPlanId(planId);
    setSelectedPlanName(planName);
    setSubscriptionError(''); // Clear any previous subscription errors

    // Reset sub-account count based on plan type
    if (planName.includes('11+ Sub-accounts')) {
      // Check if user already has any 11+ subscription
      const hasExisting11Plus = rawSubscriptions.some(
        sub => sub.planName && sub.planName.includes('11+ Sub-accounts') &&
          ['active', 'authenticated', 'created'].includes(sub.status.toLowerCase())
      );

      setIsFirst11Plus(!hasExisting11Plus);
      setSubAccountCount(hasExisting11Plus ? 1 : 11);
    } else {
      setIsFirst11Plus(false);
      setSubAccountCount(1);
    }



    // Proceed with subscription creation
    await handleCreateSubscription(planId, planName);
  };

  // Fixed: Separate function for subscription creation
  const handleCreateSubscription = async (planId: string, planName: string) => {
    setIsLoadingAction(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");

      const subscriptionState = getUserSubscriptionState(locations);

      // Validate 11+ plan logic
      if (planName.includes('11+ Sub-accounts')) {
        if (isFirst11Plus && subAccountCount !== 11) {
          toast.error('First 11+ plan must be for exactly 11 sub-accounts.');
          setIsLoadingAction(false);
          return;
        }
        if (!isFirst11Plus && subAccountCount < 1) {
          toast.error('Please enter a valid sub-account count.');
          setIsLoadingAction(false);
          return;
        }
      }

      // Compose subscription data
      const subscriptionData: any = {
        planId,
        payNow: subscriptionState === 'FREE_TRIAL' ? false : true,
      };

      // Add subAccountCount only for 11+ plans
      if (planName.includes('11+ Sub-accounts')) {
        subscriptionData.subAccountCount = subAccountCount;
      }

      console.log('Creating subscription with data:', subscriptionData);

      const response = await createSubscription(subscriptionData);
      const data: PaymentResponseData = response.data || {};

      if (response.success && data) {
        setSubscriptionError('');

        // Test mode: backend returns status:"active" directly — no Razorpay needed
        if (data.status === 'active') {
          toast.success('Test subscription activated successfully!');
          setIsPlansModalOpen(false);
          const refreshed = await getSubscribedLocations();
          setRawSubscriptions(refreshed.subscriptions);
          setLocations(
            refreshed.subscriptions.flatMap((sub: any) =>
              sub.subAccounts.map((sa: any) => ({
                id: sa._id,
                locationId: sa.locationId,
                locationName: sa.locationName,
                subscriptionDate: new Date(sub.subscriptionCreatedDate).toLocaleDateString(),
                dueDate: new Date(sub.subscriptionEndDate).toLocaleDateString(),
                status: sub.status,
                planName: sub.planName,
                razorpaySubscriptionId: sub.razorpaySubscriptionId,
                isTrialActive: false,
                trialEndDate: '',
                actualDueDate: new Date(sub.subscriptionEndDate).toLocaleDateString(),
              }))
            )
          );
          return;
        }

        if (data.orderId) {
          openRazorpay({
            orderId: data.orderId,
            amount: data.amount,
            currency: data.currency,
            subscriptionId: data.subscriptionId
          });
        } else if (data.subscriptionId) {
          openRazorpay({ subscriptionId: data.subscriptionId });
        } else {
          throw new Error('No order ID or subscription ID received from server');
        }
      } else {
        throw new Error('No order ID or subscription ID received from server');
      }
    } catch (error: any) {
      console.error("Error in subscription flow:", error);
      // Extract error message properly from the error object
      let errorMessage = error?.message || error?.response?.message || "Failed to create subscription";

      // Make error message more user-friendly
      if (errorMessage.includes("You can still add sub-accounts to your existing 11+ subscription")) {
        errorMessage = "You already have an 11+ subscription. You can add more sub-accounts to your existing subscription instead of creating a new one.";
      }

      setSubscriptionError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoadingAction(false);
    }
  };

  const handleBuySubscription = async () => {
    const subscriptionState = getUserSubscriptionState(locations);
    setIsUpgradeMode(subscriptionState === 'ACTIVE');
    setIsPlansModalOpen(true);
    setPlans([]);
    setPlansError('');
    setSubscriptionError('');
    setIsPlansLoading(true);

    try {
      const response = await getSubscriptionPlans();
      const fetchedPlans = response.data || [];
      const allowedPlanNames = [
        "Single Sub-account (Monthly)",
        "11+ Sub-accounts (Monthly)",
        "Single Sub-account (Yearly)",
        "11+ Sub-accounts (Yearly)"
      ];
      let filteredPlans = fetchedPlans.filter(plan =>
        allowedPlanNames.includes(plan.item.name)
      );

      // Remove duplicate yearly plans if they exist
      const yearlySingleIdx = filteredPlans.findIndex(p => p.item.name === 'Single Sub-Account (Yearly)');
      if (yearlySingleIdx !== -1) {
        const lastIdx = filteredPlans.map(p => p.item.name).lastIndexOf('Single Sub-Account (Yearly)');
        if (yearlySingleIdx !== lastIdx) filteredPlans.splice(lastIdx, 1);
      }

      // Add trial information to plans if user is in free trial
      if (subscriptionState === 'FREE_TRIAL') {
        filteredPlans = filteredPlans.map(plan => ({
          ...plan,
          isTrial: true,
          trialDays: 14
        })) as ExtendedPlan[];
      }

      setPlans(filteredPlans as ExtendedPlan[]);
    } catch (error: any) {
      setPlansError(error.message || 'Failed to load plans');
    } finally {
      setIsPlansLoading(false);
    }
  };

  const handleUpgradePlan = async () => {
    setIsUpgradeMode(true);
    setIsPlansModalOpen(true);
    setPlans([]);
    setPlansError('');
    setIsPlansLoading(true);

    try {
      const response = await getSubscriptionPlans();
      const fetchedPlans = response.data || [];
      const allowedPlanNames = [
        "Single Sub-account (Monthly)",
        "11+ Sub-accounts (Monthly)",
        "Single Sub-account (Yearly)",
        "11+ Sub-accounts (Yearly)"
      ];
      let filteredPlans = fetchedPlans.filter(plan =>
        allowedPlanNames.includes(plan.item.name)
      );
      const yearlySingleIdx = filteredPlans.findIndex(p => p.item.name === 'Single Sub-Account (Yearly)');
      if (yearlySingleIdx !== -1) {
        const lastIdx = filteredPlans.map(p => p.item.name).lastIndexOf('Single Sub-Account (Yearly)');
        if (yearlySingleIdx !== lastIdx) filteredPlans.splice(lastIdx, 1);
      }
      setPlans(filteredPlans as ExtendedPlan[]);
    } catch (error: any) {
      setPlansError(error.message || 'Failed to load plans');
    } finally {
      setIsPlansLoading(false);
    }
  };



  const handleActivateTestSubscription = async () => {
    setIsActivatingTestSub(true);
    try {
      await createTestSubscription();
      toast.success('Test subscription activated! You can now add a location.');
      // Refresh the locations list
      const response = await getSubscribedLocations();
      setRawSubscriptions(response.subscriptions);
      setLocations(
        response.subscriptions.flatMap((sub) =>
          sub.subAccounts.map((sa: any) => ({
            id: sa._id,
            locationId: sa.locationId,
            locationName: sa.locationName,
            subscriptionDate: new Date(sub.subscriptionCreatedDate).toLocaleDateString(),
            dueDate: new Date(sub.subscriptionEndDate).toLocaleDateString(),
            status: sub.status,
            planName: sub.planName,
            razorpaySubscriptionId: sub.razorpaySubscriptionId,
            isTrialActive: false,
            trialEndDate: '',
            actualDueDate: new Date(sub.subscriptionEndDate).toLocaleDateString(),
          }))
        )
      );
    } catch (err: any) {
      toast.error(err.message || 'Failed to activate test subscription');
    } finally {
      setIsActivatingTestSub(false);
    }
  };

  const handleAddLocationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setIsAddLocationLoading(true);

    try {
      if (!addLocationId.trim()) {
        setFormError('Oops! You forgot to add location ID.');
        setIsAddLocationLoading(false);
        return;
      }

      const response = await initiateGHLAuth(addLocationId);

      localStorage.setItem('tempLocationId', addLocationId);
      localStorage.setItem('ghlRedirectUrl', response.data);

      window.location.href = response.data;
    } catch (error: any) {
      setFormError(error.message || 'Failed to authenticate with GHL');
    } finally {
      setIsAddLocationLoading(false);
    }
  };

  const handleAddLocationCancel = () => {
    setAddLocationId("");
    setFormError("");
  };

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  // Fixed: Improved logic to find available single sub-account subscription
  const getAvailableSingleSubAccountSubscription = () => {
    return rawSubscriptions.find(sub =>
      sub.planName &&
      sub.planName.includes('Single Sub-account') &&
      ['active', 'authenticated', 'created'].includes(sub.status.toLowerCase()) &&
      (!sub.subAccounts || sub.subAccounts.length === 0)
    );
  };

  const availableSingleSub = getAvailableSingleSubAccountSubscription();
  const canAddLocation = !!availableSingleSub;

  return (
    <DashboardContainer>
      <Sidebar
        isCollapsed={isCollapsed}
        isMobileOpen={isMobileOpen}
        onMobileClose={toggleMobileMenu}
      />

      <MainContent isCollapsed={isCollapsed} isMobileOpen={isMobileOpen}>
        <TopBar isCollapsed={isCollapsed} isMobileOpen={isMobileOpen}>
          <ToggleButton onClick={() => {
            if (window.innerWidth <= 768) {
              toggleMobileMenu();
            } else {
              setIsCollapsed(!isCollapsed);
            }
          }}>
            <FontAwesomeIcon icon={faBars} />
          </ToggleButton>
          <UserProfile ref={dropdownRef} onClick={() => setIsProfileOpen(!isProfileOpen)}>
            <UserInfo>
              <UserName>{userName}</UserName>
            </UserInfo>
            <UserAvatar>{userName.charAt(0)}</UserAvatar>
            <UserDropdown isOpen={isProfileOpen}>
              <DropdownItem onClick={() => navigate('/profile')}>
                <FontAwesomeIcon icon={faUser} />
                Profile
              </DropdownItem>
              <DropdownItem onClick={handleLogout}>
                <FontAwesomeIcon icon={faSignOutAlt} />
                Logout
              </DropdownItem>
            </UserDropdown>
          </UserProfile>
        </TopBar>

        <FormContainer>
          {!isPlansModalOpen ? (
            <form onSubmit={handleAddLocationSubmit} noValidate>
              <FormGroup>
                <Label htmlFor="locationId">
                  Location ID
                  <RequiredAsterisk>*</RequiredAsterisk>
                  <InfoIconButton
                    type="button"
                    title="How to find Location ID"
                    onClick={() => setShowLocationIdInfo(v => !v)}
                  >
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </InfoIconButton>
                </Label>
                <Input
                  id="locationId"
                  type="text"
                  value={addLocationId}
                  onChange={(e) => setAddLocationId(e.target.value)}
                  placeholder="Enter location ID"
                  aria-required="true"
                  hasError={!!formError}
                  disabled={false}
                />
                {showLocationIdInfo && (
                  <InfoText style={{ color: '#ff6b6b', fontWeight: 400 }}>
                    To get a location ID from a GoHighLevel (GHL) account, navigate to the location view, then click "Settings" and then "Business Info". The location ID will be displayed in this section.
                  </InfoText>
                )}
                {formError && (
                  <ErrorText>
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    {formError}
                  </ErrorText>
                )}

              </FormGroup>

              <ActionButtonGroup>
                <ActionButton
                  type="submit"
                  variant="primary"
                  disabled={isAddLocationLoading}
                >
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  {isAddLocationLoading ? 'Processing...' : 'Add Location'}
                </ActionButton>
                <ActionButton
                  type="reset"
                  variant="primary"
                  onClick={handleAddLocationCancel}
                >
                  Cancel
                </ActionButton>
                <ActionButton
                  type="button"
                  variant="primary"
                  onClick={handleBuySubscription}
                >
                  {getUserSubscriptionState(locations) === 'ACTIVE' ? 'Upgrade Plan' : 'Buy Subscription'}
                </ActionButton>
              </ActionButtonGroup>
            </form>
          ) : (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
            }}>
              <div style={{
                background: '#f5f6fa',
                borderRadius: 24,
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                padding: '40px 32px 32px 32px',
                width: '100%',
                position: 'relative',
                margin: '0 auto',
                overflow: 'auto',
                maxHeight: '80vh'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: 32,
                  flexWrap: 'wrap'
                }}>
                  <div style={{
                    background: '#fff',
                    borderRadius: 30,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    padding: '6px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    margin: '0 auto 16px auto'
                  }}>
                    <span style={{ fontWeight: 600, color: planPeriod === 'Monthly' ? '#222' : '#888', fontSize: 18 }}>Monthly</span>
                    <label style={{ position: 'relative', display: 'inline-block', width: 48, height: 24, margin: '0 8px', verticalAlign: 'middle' }}>
                      <input
                        type="checkbox"
                        checked={planPeriod === 'Yearly'}
                        onChange={() => setPlanPeriod(planPeriod === 'Monthly' ? 'Yearly' : 'Monthly')}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{
                        position: 'absolute',
                        cursor: 'pointer',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: planPeriod === 'Yearly' ? '#ff6b6b' : '#eee',
                        borderRadius: 24,
                        transition: 'background 0.2s',
                      }}></span>
                      <span style={{
                        position: 'absolute',
                        left: planPeriod === 'Yearly' ? 24 : 2,
                        top: 2,
                        width: 20,
                        height: 20,
                        background: '#fff',
                        borderRadius: '50%',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                        transition: 'left 0.2s',
                        zIndex: 2,
                      }}></span>
                    </label>
                    <span style={{ fontWeight: 600, color: planPeriod === 'Yearly' ? '#222' : '#888', fontSize: 18 }}>Yearly</span>
                  </div>
                </div>
                <button
                  style={{
                    position: 'absolute',
                    top: 18,
                    right: 24,
                    background: 'none',
                    border: 'none',
                    color: '#ff6b6b',
                    fontSize: 28,
                    cursor: 'pointer',
                    fontWeight: 700,
                    zIndex: 10,
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                  }}
                  onClick={() => {
                    setIsPlansModalOpen(false);
                    setSubscriptionError('');
                  }}
                >
                  ×
                </button>
                {isPlansLoading ? (
                  <Loader />
                ) : plansError ? (
                  <p style={{ color: 'red' }}>{plansError}</p>
                ) : subscriptionError ? (
                  <div style={{
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '24px',
                    color: '#dc2626',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        {subscriptionError}
                      </div>
                      <button
                        onClick={() => setSubscriptionError('')}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc2626',
                          cursor: 'pointer',
                          fontSize: '16px',
                          padding: '4px',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="Clear error"
                      >
                        ×
                      </button>
                    </div>
                    {subscriptionError.includes("You already have an 11+ subscription") && (
                      <div style={{
                        background: '#f0f9ff',
                        border: '1px solid #bae6fd',
                        borderRadius: '6px',
                        padding: '12px',
                        marginTop: '8px',
                        color: '#0369a1',
                        fontSize: '13px'
                      }}>
                        <strong>💡 Suggestion:</strong> You can add more locations to your existing subscription using the "Add Location".
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                    <PlansList style={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '20px',
                      padding: '0',
                      margin: '0 auto',
                      flexWrap: 'wrap'
                    }}>
                      {plans
                        .filter(plan => plan.item.name.includes(planPeriod))
                        .sort((a, b) => {
                          const isASinglePlan = a.item.name.includes("Single Sub-Account");
                          const isBSinglePlan = b.item.name.includes("Single Sub-Account");
                          if (isASinglePlan && !isBSinglePlan) return 1;
                          if (!isASinglePlan && isBSinglePlan) return -1;
                          return 0;
                        })
                        .map((plan, idx) => {
                          const isSinglePlan = plan.item.name.includes("Single Sub-account");
                          console.log("🚀 ~ isSinglePlan:", isSinglePlan)
                          const subscriptionState = getUserSubscriptionState(locations);
                          let features = [];

                          if (isSinglePlan) {
                            features = [
                              "Unlimited WhatsApp messages",
                              "Single sub-account",
                              "24/7 Support",
                              "Message Templates",
                              "Basic Analytics"
                            ];
                          } else {
                            features = [
                              "Everything in Single Sub-Account",
                              "Minimum 11 sub-accounts",
                              "Priority Support",
                              "Advanced Analytics",
                              "Bulk Messaging Features"

                            ];
                          }

                          const price = plan.item.amount;
                          const isMonthly = plan.item.name.includes("Monthly");

                          return (
                            <PlanItem key={plan.id} style={{
                              border: '1px solid #ff6b6b',
                              borderRadius: '20px',
                              flex: '1 1 300px',
                              maxWidth: '450px',
                              minWidth: '280px',
                              margin: '0',
                              background: '#fff',
                              boxShadow: 'none',
                              padding: '32px 24px',
                              position: 'relative',
                            }}>
                              {plan.isTrial && (
                                <div style={{
                                  position: 'absolute',
                                  top: -12,
                                  right: -12,
                                  background: '#25D366',
                                  color: 'white',
                                  padding: '4px 12px',
                                  borderRadius: '16px',
                                  fontSize: '14px',
                                  fontWeight: 'bold'
                                }}>
                                  14 Days Free Trial
                                </div>
                              )}
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontWeight: 700,
                                fontSize: 22,
                                color: '#222',
                                marginBottom: 16
                              }}>
                                <span style={{
                                  display: 'inline-flex',
                                  color: '#25D366',
                                  fontSize: 24
                                }}>
                                  <FontAwesomeIcon icon={faWhatsapp} />
                                </span>
                                {plan.item.name}
                              </div>
                              <div style={{ fontSize: 40, fontWeight: 700, color: '#333', marginBottom: 0 }}>
                                ${price}
                                <span style={{ fontSize: 18, color: '#888', fontWeight: 400 }}>
                                  /{isMonthly ? 'month' : 'year'}
                                </span>
                              </div>
                              <div style={{ color: '#888', fontSize: 15, marginBottom: 24 }}>
                                Per Sub-Account
                              </div>
                              <ul style={{ color: '#ff6b6b', margin: '0 0 24px 0', paddingLeft: 0, listStyle: 'none', fontSize: 16, fontWeight: 500 }}>
                                {features.map((f, i) => (
                                  <li key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                                    <span style={{ color: '#ff6b6b', fontWeight: 700, marginRight: 8, flexShrink: 0 }}>✓</span>
                                    <span style={{ wordBreak: 'break-word', color: '#555' }}>{f}</span>
                                  </li>
                                ))}
                              </ul>

                              {/* Fixed: Sub-account count input for 11+ plans */}
                              {plan.item.name.includes('11+ Sub-accounts') && (
                                <div style={{ margin: '16px 0', padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <strong>Number of Sub-Accounts:</strong>
                                    <input
                                      type="number"
                                      min={isFirst11Plus ? 11 : 1}
                                      max={99}
                                      value={subAccountCount}
                                      disabled={isFirst11Plus}
                                      onChange={e => setSubAccountCount(Number(e.target.value))}
                                      style={{
                                        marginLeft: 8,
                                        width: 80,
                                        padding: '4px 8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px'
                                      }}
                                    />
                                  </label>
                                  {isFirst11Plus && (
                                    <div style={{ color: '#ff6b6b', fontSize: '14px', fontWeight: 500 }}>
                                      First purchase must be for exactly 11 sub-accounts
                                    </div>
                                  )}
                                  {!isFirst11Plus && (
                                    <div style={{ color: '#666', fontSize: '14px' }}>
                                      Additional sub-accounts: {subAccountCount}
                                    </div>
                                  )}
                                </div>
                              )}

                              <button
                                style={{
                                  background: '#fff',
                                  color: '#ff6b6b',
                                  border: '2px solid #ff6b6b',
                                  borderRadius: 10,
                                  padding: '14px 0',
                                  fontWeight: 700,
                                  fontSize: 18,
                                  width: '100%',
                                  marginTop: 8,
                                  cursor: 'pointer',
                                  transition: 'background 0.2s, color 0.2s',
                                }}
                                onClick={() => handleSelectPlan(plan.id, plan.item.name)}
                                onMouseOver={e => {
                                  e.currentTarget.style.background = '#ff6b6b';
                                  e.currentTarget.style.color = '#fff';
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.background = '#fff';
                                  e.currentTarget.style.color = '#ff6b6b';
                                }}
                              >
                                {(() => {
                                  switch (subscriptionState) {
                                    case 'FREE_TRIAL':
                                      return 'Start 14 Days Free Trial';
                                    case 'PENDING_PAYMENT':
                                      return 'Buy Now';
                                    case 'ACTIVE':
                                      return 'Upgrade Plan';
                                    default:
                                      return 'Select Plan';
                                  }
                                })()}
                              </button>
                            </PlanItem>
                          );
                        })}
                    </PlansList>
                  </div>
                )}
              </div>
            </div>
          )}
        </FormContainer>

        <PageContainer>
          <TableHeader>
            <TableTitle>Subscribed List</TableTitle>
            {locations.length > 0 && (
              <TableControls>
                <EntriesControl>
                  <span>Show</span>
                  <Select
                    value={entriesPerPage}
                    onChange={(e) => setEntriesPerPage(e.target.value)}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </Select>
                  <span>entries</span>
                </EntriesControl>
                <SearchControl>
                  <span>Search:</span>
                  <SearchInputWrapper>
                    <SearchInput
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search..."
                    />
                    {searchTerm && (
                      <ClearButton type="button" onClick={() => setSearchTerm('')} aria-label="Clear search">
                        ×
                      </ClearButton>
                    )}
                  </SearchInputWrapper>
                </SearchControl>
              </TableControls>
            )}
          </TableHeader>

          {isLoading ? (
            <Loader />
          ) : error && locations.length === 0 ? (
            <ErrorImageWrapper>
              <EmptyImage src={noDataFound} alt="Error loading data" />
            </ErrorImageWrapper>
          ) : filteredLocations.length === 0 ? (
            <EmptyState>
              <EmptyStateImage src={searchTerm ? noResults : noDataFound} alt="No data found" />
              {rawSubscriptions.length === 0 && (
                <div style={{
                  marginTop: '24px',
                  padding: '20px 28px',
                  background: '#fff8e1',
                  border: '1.5px dashed #ffb300',
                  borderRadius: '12px',
                  textAlign: 'center',
                  maxWidth: '420px',
                  margin: '24px auto 0'
                }}>
                  <div style={{ fontSize: '15px', color: '#856404', fontWeight: 600, marginBottom: '8px' }}>
                    🧪 Testing Mode
                  </div>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
                    No subscription found. Activate a free test subscription to proceed without payment.
                  </div>
                  <button
                    onClick={handleActivateTestSubscription}
                    disabled={isActivatingTestSub}
                    style={{
                      background: 'linear-gradient(135deg, #ff6b6b, #ff8e53)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 24px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: isActivatingTestSub ? 'not-allowed' : 'pointer',
                      opacity: isActivatingTestSub ? 0.7 : 1,
                    }}
                  >
                    {isActivatingTestSub ? 'Activating...' : 'Activate Test Subscription'}
                  </button>
                </div>
              )}
            </EmptyState>
          ) : (
            <>
              <Table>
                <thead>
                  <tr>
                    <Th>Location ID</Th>
                    <Th onClick={() => handleSort('locationName')} style={{ cursor: 'pointer' }}>
                      Location Name <span style={{ color: sortColumn === 'locationName' ? '#333' : '#bbb' }}>{getSortArrow('locationName')}</span>
                    </Th>
                    <Th onClick={() => handleSort('planName')} style={{ cursor: 'pointer' }}>
                      Plan Name <span style={{ color: sortColumn === 'planName' ? '#333' : '#bbb' }}>{getSortArrow('planName')}</span>
                    </Th>
                    <Th onClick={() => handleSort('subscriptionDate')} style={{ cursor: 'pointer' }}>
                      Subscription Date <span style={{ color: sortColumn === 'subscriptionDate' ? '#333' : '#bbb' }}>{getSortArrow('subscriptionDate')}</span>
                    </Th>
                    <Th onClick={() => handleSort('dueDate')} style={{ cursor: 'pointer' }}>
                      Due Date <span style={{ color: sortColumn === 'dueDate' ? '#333' : '#bbb' }}>{getSortArrow('dueDate')}</span>
                    </Th>
                    <Th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                      Status <span style={{ color: sortColumn === 'status' ? '#333' : '#bbb' }}>{getSortArrow('status')}</span>
                    </Th>
                    <Th>Action</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLocations.map((location) => (
                    <tr key={location.id}>
                      <Td>{location.locationId}</Td>
                      <Td>{location.locationName}</Td>
                      <Td>{location.planName}</Td>
                      <Td>{location.subscriptionDate}</Td>
                      <Td>{location.dueDate}</Td>
                      <Td>
                        <StatusBadge status={location.status}>{location.status}</StatusBadge>
                        {location.isTrialActive && (
                          <div style={{
                            marginTop: '4px',
                            fontSize: '11px',
                            color: '#ff6b6b',
                            fontWeight: '500'
                          }}>
                            ⏰ Trial Active
                          </div>
                        )}
                      </Td>
                      <Td>
                        <TableActionGroup>
                          <TableActionButton
                            variant="whatsapp"
                            onClick={() => navigate(`${APP_ROUTES.QR_SCAN}?locationId=${location.locationId}`)}
                            disabled={['cancelled', 'terminated'].includes(location.status.toLowerCase())}
                          >
                            Connect WhatsApp
                          </TableActionButton>
                          <TableActionButton
                            variant="cancel"
                            onClick={() => handleOpenCancelModal(location)}
                            disabled={['cancelled', 'terminated'].includes(location.status.toLowerCase())}
                          >
                            Cancel Subscription
                          </TableActionButton>
                        </TableActionGroup>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Pagination>
                <PaginationControls>
                  <PaginationButton disabled>Previous</PaginationButton>
                  <PaginationButton active>1</PaginationButton>
                  <PaginationButton disabled>Next</PaginationButton>
                </PaginationControls>
              </Pagination>
            </>
          )}
        </PageContainer>

        <Footer isCollapsed={isCollapsed} />
      </MainContent>

      {cancelModalOpen && selectedSubscription && (
        <ModalOverlay>
          <Modal>
            <ModalHeader>
              <ModalTitle>Cancel Subscription?</ModalTitle>
              <CloseButton onClick={handleCloseCancelModal}>×</CloseButton>
            </ModalHeader>
            <div style={{ marginBottom: 16 }}>
              <strong style={{ color: '#dc3545' }}>Important:</strong> <strong>This action can not be undone.</strong> Your subscription will end at {selectedSubscription.dueDate}. The subscription will move to cancelled state immediately.
            </div>
            {cancelError && <ErrorMessage>{cancelError}</ErrorMessage>}
            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <CancelButton style={{ background: '#f5f6fa', color: '#333', border: '1px solid #ddd' }} onClick={handleCloseCancelModal} disabled={cancelLoading}>
                No, don't
              </CancelButton>
              <CancelButton onClick={handleCancelSubmit} disabled={cancelLoading}>
                {cancelLoading ? 'Cancelling...' : 'Yes, Cancel'}
              </CancelButton>
            </div>
          </Modal>
        </ModalOverlay>
      )}



      {isLoadingAction && <Loader />}

      <LogoutConfirmDialog
        isOpen={showLogoutConfirm}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </DashboardContainer>
  );
};

export default SubscribedLocations; 